import { Box, Button, Card, Divider, Drawer, Grid, IconButton, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { finalize, map, of } from 'rxjs';

import { Close, File } from '@icons/index';
import { clientsUnitsService } from '@store/clients/units';
import { documentsService, ManageDocument } from '@store/documents';
import { DocumentKind } from '@store/files';

import SelectComponent from '@components/inputs/select/Select.component';
import SelectFileComponent from '@components/inputs/selectFile/SelectFile.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';

import { checkIfErrors, FieldValidationType, getFieldError } from '@utils/yup.utils';

import AsyncSelectWithSearchComponent from '@components/inputs/asyncSelect/AsyncSelectWithSearch.component';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import { Sizes } from '@constants/sizes.constants';
import { clientsService } from '@store/clients';
import { OrganisationType } from '@store/common/organisation.enums';
import { suppliersService } from '@store/suppliers';
import { suppliersUnitsService } from '@store/suppliers/units';
import { useSnackbar } from 'notistack';

interface ManageExecutionDrawerProps {
  fromClient?: boolean;
  isUnitClient?: boolean;
  isUnitSupplier?: boolean;
  fromSupplier?: boolean;
  isCreation?: boolean;
}

const ManageDocumentDrawer = (props: ManageExecutionDrawerProps) => {
  const { fromClient, isUnitClient, isUnitSupplier, fromSupplier, isCreation } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clientId, documentId, supplierId, clientUnitId, supplierUnitId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const handleClose = () => navigate('..', { replace: true });

  const [loading, setLoading] = useState(false);

  const [document, setDocument] = useState<ManageDocument>(isCreation ? {
    organisationType: fromClient ? OrganisationType.CLIENT : (fromSupplier ? OrganisationType.SUPPLIER : undefined),
    organisation: (fromClient && clientId) || (fromSupplier && supplierId) ? {
      name: '',
      id: clientId ? +clientId : (supplierId ? +supplierId : -1)
    } : undefined,
    organisationUnit: (fromClient && clientUnitId) || (fromSupplier && supplierUnitId) ? {
      name: '',
      id: clientUnitId ? parseInt(clientUnitId) : (supplierUnitId ? parseInt(supplierUnitId) : -1)
    } : undefined,
  } : {});

  useEffect(() => {
    if (documentId) {
      documentsService.getSelectedDocument(documentId).subscribe({
        next: (d) => setDocument(ManageDocument.fromDocument(d)),
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  }, [documentId, enqueueSnackbar]);

  if (!isCreation && !document?.id) return null;

  const updateDocument = (name: string) => (value: any) => setDocument({ ...document, [name]: value });

  const handleSave = () => {
    if (isCreation) {
      setLoading(true);
      documentsService.createDocument(document).pipe(finalize(() => setLoading(false))).subscribe({
        next: () => {
          enqueueSnackbar(t('documents.manage.success.created'), { variant: 'success' });
          navigate('..');
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    } else {
      if (!documentId) return;
      setLoading(true);
      documentsService.updateDocument(documentId, document).pipe(finalize(() => setLoading(false))).subscribe({
        next: (ud) => {
          enqueueSnackbar(t('documents.manage.success.updated'), { variant: 'success' });
          setDocument(ManageDocument.fromDocument(ud));
          navigate('..');
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  };

  const errors = {
    documentKind: getFieldError(document.documentKind, FieldValidationType.REQUIRED_STRING),

    organisationType: getFieldError(document.organisationType, FieldValidationType.REQUIRED_STRING),
    organisation: !fromClient && !fromSupplier ? getFieldError(document.organisation, FieldValidationType.REQUIRED_SELECT_ITEM) : undefined,
    startValidity: getFieldError(document.startValidity, FieldValidationType.DATE),
    endValidity: getFieldError(document.endValidity, FieldValidationType.DATE),

    documentFile: !document.documentFile && isCreation ? t('errors.fields.required') : undefined,
  };

  return (
    <Drawer anchor="right" open onClose={handleClose}>
      <Stack overflow="hidden" flex={1}>
        <Stack direction="row" justifyContent="flex-end" my="-4px" mr="-2px">
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Stack>
        <Stack pr="10px">
          <Typography fontSize={24} fontWeight="700" mb="3px">
            {t(`documents.manage.title.${isCreation ? 'add' : 'edit'}`)}
          </Typography>
        </Stack>
        <Divider variant="fullWidth" orientation="horizontal" />
        <Stack className="scrollable" flex={1} overflow="auto" pb="24px" pr="10px">
          <Stack py="20px" spacing={2}>
            <Typography fontSize={'18px'} fontWeight="700">
              {t('documents.manage.propriety')}
            </Typography>
            <Stack spacing={2}>
              <SelectComponent
                labelWidth={Sizes.drawerInputLabelWidth}
                label={'*' + t('documents.manage.type')}
                placeholder={t('documents.manage.placeholder.type')}
                handleChange={updateDocument('documentKind')}
                error={errors.documentKind}
                value={document.documentKind}
                items={DocumentKind.selectItems()} />
              {!fromClient && !fromSupplier && (
                <SelectComponent
                  labelWidth={Sizes.drawerInputLabelWidth}
                  label={'*' + t('documents.manage.organisationType')}
                  placeholder={t('documents.manage.placeholder.organisationType')}
                  handleChange={(type) => {
                    if (type !== document.organisationType) {
                      setDocument((state) => ({
                        ...state,
                        organisationType: type,
                        organisation: undefined,
                        organisationUnit: undefined
                      }));
                    }
                  }}
                  error={errors.organisationType}
                  value={document.organisationType}
                  items={OrganisationType.selectItems()} />
              )}
              {!fromClient && !fromSupplier && (
                <AsyncSelectWithSearchComponent
                  labelWidth={Sizes.drawerInputLabelWidth}
                  label={t('documents.manage.organisation')}
                  placeholder={t('global.selectOption')}
                  handleChange={(item) => {
                    updateDocument('organisation')(item ? { id: item.value, name: item.label } : undefined);
                  }}
                  value={document.organisation ? {
                    label: document.organisation?.name || '',
                    value: document.organisation?.id
                  } : undefined}
                  getOptions={(search) => {
                    switch (document.organisationType) {
                      case OrganisationType.CLIENT:
                        return clientsService.searchClients(search).pipe(
                          map((options) => options.data.map((o) => ({ label: o.name, value: o.id })))
                        );
                      case OrganisationType.SUPPLIER:
                        return suppliersService.searchSuppliers(search).pipe(
                          map((options) => options.data.map((o) => ({ label: o.name, value: o.id })))
                        );
                      default:
                        return of([]);
                    }
                  }}
                  error={errors.organisation}
                  required />
              )}
              {!isUnitClient && !isUnitSupplier && (
                <AsyncSelectWithSearchComponent
                  labelWidth={Sizes.drawerInputLabelWidth}
                  label={t('documents.manage.site')}
                  placeholder={t('global.selectOption')}
                  handleChange={(organisationUnit) =>
                    updateDocument('organisationUnit')(
                      organisationUnit ? {
                        id: organisationUnit.value,
                        name: organisationUnit.label,
                      } : undefined
                    )
                  }
                  value={document.organisationUnit ? {
                    label: document.organisationUnit.name,
                    value: document.organisationUnit.id
                  } : undefined}

                  getOptions={(search) => {
                    switch (document.organisationType) {
                      case OrganisationType.CLIENT:
                        return clientsUnitsService.searchClientsUnits(search, document.organisation?.id ? [document.organisation.id] : undefined).pipe(
                          map((options) => options.data.map((o) => ({ label: o.name, value: o.id })))
                        );
                      case OrganisationType.SUPPLIER:
                        return suppliersUnitsService.searchSuppliersUnits(search, document.organisation?.id ? [document.organisation.id] : undefined).pipe(
                          map((options) => options.data.map((o) => ({ label: o.name, value: o.id })))
                        );
                      default:
                        return of([]);
                    }
                  }}
                  disabled={!document.organisation}
                />
              )}
              <DatePickerComponent
                labelWidth={Sizes.drawerInputLabelWidth}
                label={t('documents.manage.begin')}
                placeholder={t('documents.manage.placeholder.begin')}
                handleChange={updateDocument('startValidity')}
                value={document.startValidity}
                error={errors.startValidity} />
              <DatePickerComponent
                labelWidth={Sizes.drawerInputLabelWidth}
                label={t('documents.manage.end')}
                placeholder={t('documents.manage.placeholder.end')}
                handleChange={updateDocument('endValidity')}
                value={document.endValidity}
                error={errors.endValidity} />
              <NumberTextFieldComponent
                labelWidth={Sizes.drawerInputLabelWidth}
                label={t('documents.manage.amount')}
                placeholder={t('documents.manage.placeholder.amount')}
                value={(document?.amount ?? 0) / 100}
                handleChange={(a) => {
                  if (typeof a !== 'undefined') {
                    const decimalAmount = parseFloat(a.toString().replace(',', '.'));
                    const centAmount = Math.round(decimalAmount * 100);
                    updateDocument('amount')(centAmount);
                  }
                }}
              />
              <TextFieldComponent
                labelWidth={Sizes.drawerInputLabelWidth}
                label={t('documents.manage.reference')}
                placeholder={t('documents.manage.placeholder.reference')}
                value={document.reference ?? ''}
                handleChange={updateDocument('reference')}
              />

            </Stack>
          </Stack>
          <Divider variant="fullWidth" orientation="horizontal" />
          <Stack py="20px" spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography fontSize="18px" fontWeight="700">
                {t('documents.manage.document')}
              </Typography>
              {isCreation && (
                <SelectFileComponent
                  handleFileSelected={updateDocument('documentFile')} />
              )}
            </Stack>
            <Box>
              {((!isCreation && !!document.name) || (isCreation && !!document.documentFile)) && (
                <Grid container spacing={2}>
                  <Grid item>
                    <Card variant="document">
                      <File />
                      <Stack mt="5px">
                        <Typography
                          textAlign="center"
                          fontSize={10}
                          fontWeight="700"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: '2',
                            WebkitBoxOrient: 'vertical',
                          }}>
                          {document.documentFile?.name || document.name}
                        </Typography>
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Stack>
          <Stack justifyContent={'space-between'} spacing={4} flex={1}>
            <Divider variant="fullWidth" orientation="horizontal" />
            <Box alignSelf="flex-end">
              <Button variant="contained" color="primary" onClick={handleSave}
                disabled={checkIfErrors(errors) || loading}>
                {t('global.save')}
              </Button>
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default ManageDocumentDrawer;
